import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reveal, SectionLabel } from './Section';
import { CERT_COMPLETION, CERT_END, CERT_MODULES } from '../data/content';
import { X, ChevronLeft, ChevronRight, FileCheck2 } from 'lucide-react';

const GROUPS = [
  { key:'completion', label:'Course Completion', badge:'2', desc:'Dual, co-branded program certification.', data: CERT_COMPLETION },
  { key:'end',        label:'End-to-End Industry Projects', badge:'2', desc:'Complete real-world projects, powered by Intellectra AI.', data: CERT_END },
  { key:'module',     label:'Module-Based Project Certificates', badge:'7', desc:'A real project certificate for every tool you master.', data: CERT_MODULES },
];

export default function Certifications() {
  const [lb, setLb] = useState({ group:null, idx:0 });
  const open = lb.group !== null;
  const curArr = open ? GROUPS.find(g => g.key === lb.group).data : [];
  const next = () => setLb(s => ({ ...s, idx: (s.idx + 1) % curArr.length }));
  const prev = () => setLb(s => ({ ...s, idx: (s.idx - 1 + curArr.length) % curArr.length }));
  const close = () => setLb({ group:null, idx:0 });

  return (
    <section id="certificates" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal><SectionLabel>Certifications</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-[42px] sm:text-[56px] lg:text-[68px] leading-[1.02] font-semibold text-[#293891] mt-6 max-w-4xl">
            Certificates that <span className="text-[#E63329]">open doors.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#293891]/75">
            You don't finish with one certificate — you finish with a stack. Dual course-completion certification (co-branded with Texas Tech University), real end-to-end project certificates for ML/AI work, and a certificate for every module-based project — all from our technology partner Intellectra AI.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3">
          {['ExcellenC','Texas Tech University','Intellectra AI'].map(b => (
            <span key={b} className="px-4 py-2 rounded-full border border-[#293891]/20 text-[12px] font-semibold text-[#293891] bg-white">{b}</span>
          ))}
        </div>

        {/* Stacked groups */}
        <div className="mt-14 space-y-10">
          {GROUPS.map((g, gi) => (
            <Reveal key={g.key} delay={gi * 0.05}>
              <div className="bg-white border border-[#293891]/10 rounded-3xl p-6 lg:p-10">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-[24px] sm:text-[30px] font-semibold text-[#293891]">{g.label}</h3>
                      <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#E63329]/10 text-[#E63329] font-semibold">{g.badge}</span>
                    </div>
                    <p className="text-[13.5px] text-[#293891]/70 mt-2">{g.desc}</p>
                  </div>
                </div>

                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {g.data.map((c, i) => (
                    <button key={i} onClick={() => setLb({ group: g.key, idx: i })} className="group text-left">
                      <div className="overflow-hidden rounded-2xl border border-[#293891]/10 bg-[#F4F5FB]">
                        <img src={c.src} alt={c.sub} className="w-full h-56 object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"/>
                      </div>
                      <div className="mt-4">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#E63329] font-semibold">{c.title}</div>
                        <div className="font-display text-[17px] font-semibold text-[#293891] mt-1 group-hover:text-[#E63329] transition-colors">{c.sub}</div>
                        {c.issuer && <div className="text-[12px] text-[#293891]/55 mt-1">{c.issuer}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Internship Letters */}
        <Reveal delay={0.1}>
          <div className="mt-14 bg-white border border-[#293891]/10 rounded-3xl p-8 lg:p-10">
            <div className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#293891]/70">Also Included</div>
            <h3 className="font-display text-[28px] sm:text-[36px] font-semibold text-[#293891] mt-3">Internship Letters — <span className="text-[#E63329]">Included</span></h3>
            <p className="text-[14px] text-[#293891]/75 mt-3 max-w-2xl">Beyond certificates, your enrollment comes with official internship documentation for the entire program duration.</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {['Internship Offer Letter·Issued during enrollment','Internship Completion Letter·Issued at course completion','Internship Experience Letter·Covering the entire course duration'].map((s,i) => {
                const [title, note] = s.split('·');
                return (
                  <div key={i} className="card-lift bg-[#F4F5FB] border border-[#293891]/10 rounded-2xl p-6">
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-[#293891]" style={{background:'rgba(41,56,145,0.08)'}}>
                      <FileCheck2 size={20}/>
                    </div>
                    <div className="font-display text-[17px] font-semibold text-[#293891] mt-4">{title}</div>
                    <div className="text-[12.5px] text-[#293891]/65 mt-1">{note}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={close}>
            <button className="absolute top-6 right-6 text-white/85 hover:text-white" onClick={close}><X size={28}/></button>
            <button className="absolute left-6 text-white/85 hover:text-white" onClick={(e)=>{e.stopPropagation(); prev();}}><ChevronLeft size={40}/></button>
            <button className="absolute right-6 text-white/85 hover:text-white" onClick={(e)=>{e.stopPropagation(); next();}}><ChevronRight size={40}/></button>
            <motion.img key={lb.idx} initial={{opacity:0, scale:0.96}} animate={{opacity:1, scale:1}} exit={{opacity:0}} src={curArr[lb.idx].src} alt="" className="max-h-[85vh] max-w-[92vw] rounded-xl shadow-2xl bg-white" onClick={e=>e.stopPropagation()}/>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
